// ABOUTME: Chat Runtime Provider using useLangGraphRuntime for LangGraph backend
// ABOUTME: Integrates with FastAPI backend streaming LangGraph messages
// ABOUTME: See backend/LANGGRAPH_ADAPTER_CONTRACT.md for message format details

"use client";

import { AssistantRuntimeProvider } from "@assistant-ui/react";
import { useLangGraphRuntime } from "@assistant-ui/react-langgraph";
import { TooltipProvider } from "@/components/ui/tooltip";
import { getThreadState, sendMessage } from "@/lib/chatApi";
import { ReactNode } from "react";
import { v4 as uuidv4 } from "uuid";

const THREAD_ID_KEY = "current_thread_id";

// Get or create thread ID - must be synchronous and stable
const getOrCreateThreadId = () => {
  let threadId = localStorage.getItem(THREAD_ID_KEY);
  if (!threadId) {
    threadId = uuidv4();
    localStorage.setItem(THREAD_ID_KEY, threadId);
    console.log("[ChatRuntime] Generated new thread ID:", threadId);
  }
  return threadId;
};

export function ChatRuntimeProvider({ children }: { children: ReactNode }) {
  const runtime = useLangGraphRuntime({
    stream: async function* (messages) {
      // Get stable thread ID from localStorage
      const threadId = getOrCreateThreadId();
      console.log("[ChatRuntime] Streaming with thread ID:", threadId);

      // Yield from the sendMessage generator
      yield* sendMessage({
        threadId,
        messages,
      });
    },
    create: async () => {
      // Create new thread
      const threadId = uuidv4();
      localStorage.setItem(THREAD_ID_KEY, threadId);
      console.log("[ChatRuntime] create() - Created new thread:", threadId);

      return { externalId: threadId };
    },
    load: async (externalId) => {
      console.log("[ChatRuntime] load() called with externalId:", externalId);

      try {
        const state = await getThreadState(externalId);
        const messages = (state.values as { messages?: any[] }).messages ?? [];

        console.log("[ChatRuntime] Loaded", messages.length, "messages from backend");

        // Return ALL messages including tool messages for proper matching
        return {
          messages,
          interrupts: state.tasks?.[0]?.interrupts ?? [],
        };
      } catch (error) {
        console.log("[ChatRuntime] Thread not found in backend, returning empty state");
        return {
          messages: [],
          interrupts: [],
        };
      }
    },
  });

  return (
    <TooltipProvider>
      <AssistantRuntimeProvider runtime={runtime}>
        {children}
      </AssistantRuntimeProvider>
    </TooltipProvider>
  );
}
